// import React from 'react'
import { cn } from '@/shared/lib/utils'

interface AvatarWithInitialsProps {
  firstName?: string
  lastName?: string
  name?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
}

// Generate consistent pastel color based on name
function getAvatarColor(name: string): string {
  const pastelColors = [
    'bg-red-200 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    'bg-orange-200 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    'bg-amber-200 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    'bg-yellow-200 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    'bg-lime-200 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300',
    'bg-green-200 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    'bg-emerald-200 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
    'bg-teal-200 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
    'bg-cyan-200 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
    'bg-sky-200 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
    'bg-blue-200 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    'bg-indigo-200 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
    'bg-violet-200 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
    'bg-purple-200 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    'bg-fuchsia-200 text-fuchsia-800 dark:bg-fuchsia-900/30 dark:text-fuchsia-300',
    'bg-pink-200 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
    'bg-rose-200 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
  ]

  // Generate hash from name for consistent color
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }

  const color = pastelColors[Math.abs(hash) % pastelColors.length]
  return color ?? pastelColors[0] ?? 'bg-slate-200 text-primary dark:text-slate-200'
}

// Get initials from name
function getInitials(firstName?: string, lastName?: string, fullName?: string): string {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }

  if (fullName) {
    const parts = fullName.trim().split(/\s+/)
    if (parts.length >= 2) {
      return `${parts[0]?.charAt(0)}${parts[parts.length - 1]?.charAt(0)}`.toUpperCase()
    }
    return fullName.charAt(0).toUpperCase()
  }

  if (firstName) {
    return firstName.charAt(0).toUpperCase()
  }

  return '?'
}

const sizeClasses = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
}

export function AvatarWithInitials({
  firstName,
  lastName,
  name,
  size = 'md',
  className
}: AvatarWithInitialsProps) {
  const initials = getInitials(firstName, lastName, name)
  const colorClass = getAvatarColor(name || `${firstName} ${lastName}` || 'User')

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full font-semibold',
        sizeClasses[size],
        colorClass,
        className
      )}
    >
      {initials}
    </div>
  )
}
